/**
 * Post-upload celebration screen.
 *
 * Shown after a successful scorecard upload. Displays confetti animation,
 * scorecard preview, game info, share buttons, and enrichment nudge.
 */
import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { FeedbackNugget } from '../feedback/FeedbackNugget';

interface CelebrationScreenProps {
  /** The uploaded scorecard's ID */
  scorecardId: number | null;
  /** Preview URL for the uploaded image */
  previewUrl: string | null;
  /** Game label (e.g., "ATL 5, NYM 2") */
  gameLabel: string;
  /** Game detail (e.g., "Feb 7, 2026 · Truist Park") */
  gameDetail: string;
  /** Whether scoring metadata was sparse (show enrichment nudge) */
  isSparse: boolean;
  /** Close the celebration and modal */
  onClose: () => void;
  /** Reset form to upload another scorecard */
  onUploadAnother: () => void;
  /** When true, show "Return to Game" instead of "Upload Another" */
  isGamedayContext?: boolean;
}

export function CelebrationScreen({
  scorecardId,
  previewUrl,
  gameLabel,
  gameDetail,
  isSparse,
  onClose,
  onUploadAnother,
  isGamedayContext = false,
}: CelebrationScreenProps) {
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);

  // Fire confetti on mount
  useEffect(() => {
    const duration = 2500;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ['#B8860B', '#D4A832', '#2C2416', '#E8E0D4'],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ['#B8860B', '#D4A832', '#2C2416', '#E8E0D4'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    requestAnimationFrame(frame);
  }, []);

  const scorecardUrl = scorecardId
    ? `${window.location.origin}/scorecards/${scorecardId}`
    : window.location.href;

  const shareText = `I scored ${gameLabel}! Check out my scorecard on The Linescore.`;

  const handleShareX = useCallback(() => {
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(scorecardUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [shareText, scorecardUrl]);

  const handleShareFacebook = useCallback(() => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(scorecardUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [scorecardUrl]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(scorecardUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback: select and copy
      const input = document.createElement('input');
      input.value = scorecardUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [scorecardUrl]);

  const handleViewScorebook = () => {
    onClose();
    navigate('/scorecards/my');
  };

  return (
    <div className="overflow-hidden">
      {/* Success header */}
      <div className="bg-ink px-6 py-6 text-center">
        <div className="text-[32px] mb-2">&#9918;</div>
        <h2 className="font-display text-[22px] font-black text-linen mb-1">
          Added to the Archive
        </h2>
        <p className="font-serif text-[13px] text-warm-gray-light">
          {gameDetail
            ? `${gameDetail} — Your scorecard is live.`
            : 'Your scorecard is live. Share it with the world.'}
        </p>
      </div>

      <div className="px-6 py-5">
        {/* Share preview */}
        <div className="bg-ink rounded-[6px] p-4 mb-5 flex gap-4 items-center">
          {/* Preview thumbnail */}
          <div className="w-[140px] h-[175px] bg-[#1A1A1A] rounded flex-shrink-0 flex flex-col overflow-hidden border border-[#333]">
            {previewUrl ? (
              <>
                <div className="flex-1 overflow-hidden">
                  <img
                    src={previewUrl}
                    alt="Scorecard preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-ink border-t-2 border-gold px-2 py-1.5">
                  <div className="font-sans text-[8px] text-gold tracking-[1.5px] uppercase font-bold">
                    See My Linescore
                  </div>
                  <div className="font-display text-[9px] text-linen font-bold truncate">
                    {gameLabel}
                  </div>
                  <div className="font-sans text-[7px] text-warm-gray-light">thelinescore.com</div>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 bg-parchment flex items-center justify-center text-[28px] opacity-40">
                  &#128203;
                </div>
                <div className="bg-ink border-t-2 border-gold px-2 py-1.5">
                  <div className="font-sans text-[8px] text-gold tracking-[1.5px] uppercase font-bold">
                    See My Linescore
                  </div>
                  <div className="font-display text-[9px] text-linen font-bold truncate">
                    {gameLabel}
                  </div>
                  <div className="font-sans text-[7px] text-warm-gray-light">thelinescore.com</div>
                </div>
              </>
            )}
          </div>

          <div>
            <div className="font-sans text-[10px] tracking-[2px] text-gold uppercase mb-1.5">
              Share Preview
            </div>
            <p className="font-serif text-[13px] text-warm-gray-light leading-[1.5]">
              This branded image will appear when you share your scorecard on social media.
            </p>
          </div>
        </div>

        {/* Share buttons */}
        <div className="font-sans text-[10px] tracking-[2px] text-warm-gray uppercase font-bold mb-2.5">
          Share Your Scorecard
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={handleShareX}
            className="flex items-center justify-center gap-2 bg-black text-white font-sans text-[13px] font-semibold py-3 rounded hover:opacity-90 transition-opacity"
            aria-label="Share scorecard on X (Twitter)"
          >
            <span className="text-[16px]">&#120143;</span>X (Twitter)
          </button>
          <button
            onClick={handleShareFacebook}
            className="flex items-center justify-center gap-2 bg-[#1877F2] text-white font-sans text-[13px] font-semibold py-3 rounded hover:opacity-90 transition-opacity"
            aria-label="Share scorecard on Facebook"
          >
            <span className="text-[16px] font-bold">f</span>
            Facebook
          </button>
          <button
            onClick={handleCopyLink}
            className="flex items-center justify-center gap-2 bg-espresso text-white font-sans text-[13px] font-semibold py-3 rounded hover:opacity-90 transition-opacity col-span-2"
            aria-label="Copy scorecard link to clipboard"
          >
            {linkCopied ? 'Link Copied!' : 'Copy Link'}
          </button>
        </div>

        {/* Enrichment nudge (if sparse metadata) */}
        {isSparse && (
          <div className="bg-warning-bg border border-parchment rounded p-3 mb-4">
            <p className="font-sans text-[12px] text-espresso">
              Want to add more details? You can edit your scorecard anytime — add a caption, pick
              your MVP, or update scoring notes from{' '}
              <button
                onClick={handleViewScorebook}
                className="text-gold-text font-semibold hover:underline"
              >
                My Scorebook
              </button>
              .
            </p>
          </div>
        )}

        {/* Beta feedback nugget */}
        <FeedbackNugget
          feature="Upload Flow"
          featureKey="nugget_upload"
          flagKey="beta_feedback_upload_enabled"
          question="How was the upload experience?"
        />
      </div>

      {/* Footer actions */}
      <div className="px-6 pb-5 flex gap-2">
        <button
          onClick={isGamedayContext ? onClose : onUploadAnother}
          className="flex-1 bg-gold text-ink font-sans text-[13px] font-bold py-2.5 rounded hover:opacity-90 transition-opacity"
        >
          {isGamedayContext ? 'Return to Game' : 'Upload Another'}
        </button>
        <button
          onClick={handleViewScorebook}
          className="flex-1 bg-ink text-linen font-sans text-[13px] font-semibold py-2.5 rounded hover:opacity-90 transition-opacity"
        >
          View My Scorebook
        </button>
      </div>
    </div>
  );
}

export default CelebrationScreen;
